import { useEffect, useState } from "react";
import clsx from "clsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchCustomerCrmState,
  updateChurnRiskFlag,
  type CustomerCrmState,
} from "../../../helpers/api/crm";

type Level = "yellow" | "red" | null;

interface Props {
  customerId: string;
}

const LEVEL_STYLES: Record<Exclude<Level, null>, string> = {
  yellow: "bg-amber-100 text-amber-800 border-amber-300",
  red: "bg-rose-100 text-rose-800 border-rose-300",
};

/**
 * Manual CSM override that takes priority over the auto-derived churnRisk in
 * the customer list. Pairs a level (yellow/red) with a free-text reason so
 * the next CSM on the account knows why the flag is set.
 */
export function ChurnRiskFlagSection({ customerId }: Props) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<CustomerCrmState>({
    queryKey: ["crm", "customers", customerId, "crm-state"],
    queryFn: () => fetchCustomerCrmState(customerId),
  });

  const [level, setLevel] = useState<Level>(null);
  const [reason, setReason] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data && !dirty) {
      setLevel(data.churnRiskFlag.level);
      setReason(data.churnRiskFlag.reason ?? "");
    }
  }, [data, dirty]);

  const save = useMutation({
    mutationFn: () =>
      updateChurnRiskFlag(customerId, {
        level,
        reason: reason.trim() ? reason.trim() : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["crm", "customers", customerId, "crm-state"],
      });
      qc.invalidateQueries({ queryKey: ["crm", "customers"] });
      setDirty(false);
    },
  });

  return (
    <section className="bg-white rounded-xl border border-border-secondary shadow-sm flex flex-col">
      <div className="px-4 py-3 border-b border-border-secondary">
        <h2 className="text-sm font-semibold text-content-primary">
          Churn-Risk Flag
        </h2>
        <p className="text-[10px] text-content-tertiary mt-0.5">
          manual override — takes priority over auto-detect
        </p>
      </div>

      <div className="flex-1 p-4 flex flex-col">
        {isLoading ? (
          <div className="h-4 w-28 bg-gray-100 rounded animate-pulse" />
        ) : (
          <>
            <div className="flex items-center gap-1.5 mb-3">
              {(
                [
                  { val: null as Level, label: "None" },
                  { val: "yellow" as Level, label: "Yellow" },
                  { val: "red" as Level, label: "Red" },
                ] as const
              ).map(({ val, label }) => {
                const active = level === val;
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => {
                      setLevel(val);
                      setDirty(true);
                    }}
                    className={clsx(
                      "text-[11px] px-2.5 py-1 rounded-full border transition-colors font-medium",
                      active && val && LEVEL_STYLES[val],
                      active &&
                        !val &&
                        "bg-gray-100 text-content-primary border-gray-300",
                      !active &&
                        "bg-white text-content-tertiary border-border-secondary hover:text-content-primary hover:border-gray-300",
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setDirty(true);
              }}
              rows={2}
              placeholder={level ? "Why is this flagged?" : "Set a level first"}
              disabled={!level}
              className="w-full text-sm px-2.5 py-2 border border-border-secondary rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-none flex-1 disabled:bg-gray-50 disabled:cursor-not-allowed"
            />

            <div className="flex items-center justify-end gap-2 mt-2">
              {save.isError && (
                <span className="text-[11px] text-rose-600">
                  Failed to save
                </span>
              )}
              <button
                type="button"
                disabled={!dirty || save.isPending}
                onClick={() => save.mutate()}
                className="text-xs px-3 py-1 bg-primary text-white rounded-md disabled:opacity-50 font-medium"
              >
                {save.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
